import React, {useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {InteractionManager, Keyboard, Platform, Pressable, StyleSheet, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';

import {Icon} from '../../components/Icon';
import Button from '../../components/ui-controls/Button';
import TextInput, {TextInputRef} from '../../components/ui-controls/form/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventScreenName, EventSourceType} from '../../utils/analytics/events';

import TogglePasswordVisibleIconButton from './components/TogglePasswordVisibleIconButton';
import {LoginFormData, loginSchema} from './schemas/authSchemas';

const LoginScreen = ({navigation, route}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_LOGIN>) => {
  const {userStore, shopSelectionStore} = useStores();
  const tabBarPadding = useTabBarBottomPadding();

  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [isPasswordIncorrect, setPasswordIncorrect] = useState(false);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [formKey, setFormKey] = useState(0);

  const password = useRef<TextInputRef>(null);

  const {
    control,
    handleSubmit,
    formState: {errors, touchedFields},
    watch,
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: route.params?.email || '',
      password: '',
    },
    mode: 'onBlur',
  });

  // DIS-474: Reset form state when screen gains focus to prevent stale state
  // in automation tests and ensure clean slate on each visit.
  // Note: Using formKey to force ContentView remount AND reset() to clear form state.
  // Both are needed because TextInput uses defaultValue (uncontrolled).
  // This approach works on both mobile (Tab navigator) and desktop (Stack navigator).
  useFocusEffect(
    React.useCallback(() => {
      reset({
        email: route.params?.email || '',
        password: '',
      });
      setFormKey(prev => prev + 1);
      setPasswordVisible(false);
      setPasswordIncorrect(false);
      setSubmitting(false);

      // DIS-1518: Dismiss the keyboard when leaving so the screen we navigate
      // back to is not laid out with a stale keyboard inset (content pushed
      // off the top of the screen).
      return () => Keyboard.dismiss();
    }, [reset, route.params?.email]),
  );

  // DIS-1518: Arriving from the email-entry screen (email prefilled), focus the
  // password field and raise the keyboard once the navigation transition settles.
  // Focusing synchronously on mount does not reliably show the Android keyboard.
  React.useEffect(() => {
    if (!route.params?.email) return;
    const task = InteractionManager.runAfterInteractions(() => {
      password.current?.focus();
    });
    return () => task.cancel();
  }, [formKey, route.params?.email]);

  const handleTogglePasswordVisible = () => {
    setPasswordVisible(!isPasswordVisible);
  };

  const onSubmit = async (values: LoginFormData) => {
    setSubmitting(true);
    setPasswordIncorrect(false);
    const success = await userStore.login(values);
    if (success) {
      Analytics.logEvent({
        eventName: EventName.Login,
        parameters: {
          screen_name: EventScreenName.Login,
          source_type: EventSourceType.Button,
          method: 'email',
        },
      });
      userStore.afterLogin();
    } else {
      setPasswordIncorrect(true);
    }
    setSubmitting(false);
  };

  const handleInitiatePasswordReset = async () => {
    const emailValue = watch('email');
    setSubmitting(true);
    if (Platform.OS === 'android') Keyboard.dismiss();
    await userStore.initiatePasswordReset(emailValue, () => {
      setSubmitting(false);
      navigation.navigate(shopSelectionStore.homeAppRoute as AppRoute.HOME);
    });

    Analytics.logEvent({
      eventName: EventName.PasswordResetRequest,
      parameters: {
        screen_name: EventScreenName.Login,
      },
    });
  };

  const emailValue = watch('email');

  return (
    <ContentView
      key={formKey}
      testID="login-screen"
      style={styles.main}
      parentStyle={{paddingBottom: tabBarPadding}}>
      <View>
        <Controller
          control={control}
          name="email"
          render={({field: {onChange, onBlur, value}}) => (
            <TextInput
              autoCapitalize="none"
              autoComplete="email"
              blurOnSubmit={false}
              defaultValue={value}
              errorMessageWhenTouched={errors.email?.message}
              keyboardType="email-address"
              label="Email Address"
              leftIcon={<Icon name="envelope" iconStyle="regular" />}
              onBlur={onBlur}
              onChangeText={onChange}
              onSubmitEditing={() => password.current?.focus()}
              placeholder="Enter your email"
              touched={touchedFields.email}
            />
          )}
        />
      </View>
      <View>
        <Controller
          control={control}
          name="password"
          render={({field: {onChange, onBlur}}) => (
            <TextInput
              autoCapitalize="none"
              autoComplete="password"
              errorMessageWhenTouched={errors.password?.message}
              label="Password"
              leftIcon={<Icon name="key" />}
              rightIcon={
                <TogglePasswordVisibleIconButton
                  isPasswordVisible={isPasswordVisible}
                  onPress={handleTogglePasswordVisible}
                />
              }
              onBlur={onBlur}
              onChangeText={val => {
                setPasswordIncorrect(false);
                onChange(val);
              }}
              onSubmitEditing={handleSubmit(onSubmit)}
              placeholder="Enter your password"
              ref={password}
              returnKeyLabel="go"
              returnKeyType="go"
              secureTextEntry={!isPasswordVisible}
              touched={touchedFields.password}
            />
          )}
        />
        {!!isPasswordIncorrect && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>Password incorrect</Text>
          </View>
        )}
      </View>
      <Button
        containerStyle={{alignSelf: 'center'}}
        onPress={handleSubmit(onSubmit)}
        title="Log in"
        loading={isSubmitting}
        disabled={isSubmitting}
      />
      {!!emailValue && !errors.email && (
        <View style={styles.resetOuter}>
          <Pressable style={{alignSelf: 'center'}} onPress={handleInitiatePasswordReset}>
            <Text style={styles.resetLink}>Request password reset</Text>
          </Pressable>
        </View>
      )}
    </ContentView>
  );
};

const styles = StyleSheet.create({
  main: {
    flex: 1,
    alignItems: 'stretch',
    ...Platform.select({
      native: {
        justifyContent: 'flex-start',
      },
      web: {
        justifyContent: 'center',
      },
    }),
  },
  errorContainer: {
    alignSelf: 'center',
    backgroundColor: '#FEE2E2',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#EF4444',
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginVertical: 12,
  },
  errorText: {
    color: '#DC2626',
    fontSize: 14,
    fontWeight: '600',
  },
  resetOuter: {
    alignItems: 'center',
    marginTop: 16,
  },
  resetLink: {
    color: theme.colour.text.heading.primary,
    fontSize: 14,
  },
});

export default LoginScreen;
